import { useMemo } from 'react'

import { useGetLatestCompletedAnalysisQuery } from '../../../api/analysis'
import { AnalysisNotesMap, useGetAnalysisNotesQuery } from '../../../api/analysis-notes'
import { useGetInternalGameDocumentQuery } from '../../../api/games'
import { InternalGameScreenshot, useGetInternalGameScreenshotsQuery } from '../../../api/screenshot-links'
import { Benchmark, InternalAnalysis } from '../types/InternalAnalysis'

export type BenchmarkColumn = {
  benchmark: Benchmark
  gameName: string
  analysis: InternalAnalysis
  notes?: AnalysisNotesMap
  screenshots?: InternalGameScreenshot[]
  // The notes or screenshots couldn't be read.
  failed: boolean
}

const noBenchmark: Benchmark = { gameId: '', marketIso: '' }

const useBenchmarkColumn = (benchmark?: Benchmark): BenchmarkColumn | undefined => {
  const skip = !benchmark
  const gameId = benchmark?.gameId ?? ''
  const gameQuery = useGetInternalGameDocumentQuery(gameId, { skip })
  const analysisQuery = useGetLatestCompletedAnalysisQuery(benchmark ?? noBenchmark, { skip })
  const notesQuery = useGetAnalysisNotesQuery(gameId, { skip })
  const screenshotsQuery = useGetInternalGameScreenshotsQuery(gameId, { skip })

  return useMemo(() => {
    const analysis = analysisQuery.data
    // Legacy compares only with games that have a completed, answered analysis in the market.
    if (!benchmark || !analysis || !Object.keys(analysis.answers ?? {}).length) return undefined
    return {
      benchmark,
      gameName: gameQuery.data?.name ?? benchmark.gameId,
      analysis,
      notes: notesQuery.data,
      screenshots: screenshotsQuery.data,
      failed: notesQuery.isError || screenshotsQuery.isError,
    }
  }, [analysisQuery.data, benchmark, gameQuery.data?.name, notesQuery.data, notesQuery.isError, screenshotsQuery.data, screenshotsQuery.isError])
}

// A fixed number of reads, the benchmark maximum.
export const useBenchmarkColumns = (benchmarks: Benchmark[]): BenchmarkColumn[] => {
  const first = useBenchmarkColumn(benchmarks[0])
  const second = useBenchmarkColumn(benchmarks[1])
  const third = useBenchmarkColumn(benchmarks[2])
  return useMemo(() => [first, second, third].filter((column): column is BenchmarkColumn => !!column), [first, second, third])
}
